import { useEffect, useMemo, useState } from 'react'
import { useCalendarEvents } from '../../Calendar/useCalendarEvents'
import { addDays, startOfWeek, toIsoDate } from '../../Calendar/calendarHelpers'
import { subscribeMeetings } from '../../../services/meetingService'
import { subscribeHuddleSessions, type HuddleSession } from '../../../services/huddleSessionService'
import { useChat } from '../../../context/useChat'
import { useAuth } from '../../../context/useAuth'
import { useTimeline } from '../../../context/useTimeline'
import type { CalendarBoardFilterState } from '../../../types/schema'
import type { MeetingRecord } from '../../../types/meetings'

const visibleFilterState: CalendarBoardFilterState = { explicit: {}, showAll: true }

const meetingDate = (value: MeetingRecord['scheduledAt']): Date | null => {
  if (!value) return null
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value
  if (typeof (value as { toDate?: () => Date }).toDate === 'function') {
    const date = (value as { toDate: () => Date }).toDate()
    return Number.isNaN(date.getTime()) ? null : date
  }
  const parsed = new Date(value as unknown as string)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

export type NexusCounts = {
  unreadThreads: number
  eventsToday: number
  eventsThisWeek: number
  meetingsUpcoming: number
  inHuddle: number
  onlineCount: number
  typingCount: number
}

const EMPTY_MEETINGS: MeetingRecord[] = []
const EMPTY_HUDDLE_SESSIONS: HuddleSession[] = []

export const useNexusCounts = (workspaceId: string | null | undefined, opts?: { skipMeetings?: boolean }): NexusCounts => {
  const { unreadByChannel, huddlePresence, viewerPresence, typingPresence } = useChat()
  const { isAccessRestricted } = useTimeline()
  const { user } = useAuth()
  const [meetings, setMeetings] = useState<MeetingRecord[]>([])
  const [huddleSessions, setHuddleSessions] = useState<HuddleSession[]>([])

  const uid = user?.uid ?? ''

  const skip = !workspaceId || !!opts?.skipMeetings

  useEffect(() => {
    if (skip) {
      return undefined
    }
    const stopMeetings = subscribeMeetings(workspaceId, setMeetings, () => setMeetings([]), { restricted: !!isAccessRestricted, uid })
    const stopHuddleSessions = subscribeHuddleSessions(workspaceId, setHuddleSessions, () => setHuddleSessions([]), { restricted: !!isAccessRestricted, uid })
    return () => {
      stopMeetings()
      stopHuddleSessions()
      setMeetings([])
      setHuddleSessions([])
    }
  }, [skip, workspaceId, isAccessRestricted, uid])

  const effectiveMeetings = skip ? EMPTY_MEETINGS : meetings
  const effectiveHuddleSessions = skip ? EMPTY_HUDDLE_SESSIONS : huddleSessions

  const today = useMemo(() => new Date(), [])
  const todayIso = toIsoDate(today)
  const weekStartIso = toIsoDate(startOfWeek(today))
  const weekEndIso = toIsoDate(addDays(startOfWeek(today), 6))

  const events = useCalendarEvents({
    meetings: effectiveMeetings,
    huddleSessions: effectiveHuddleSessions,
    activeBoard: 'DASHBOARD',
    boardScope: { mode: 'custom', boardKeys: [] },
    filterState: visibleFilterState,
    windowStartIso: weekStartIso,
    windowEndIso: weekEndIso,
  })

  const upcomingMeetings = useMemo(() => {
    const startOfToday = new Date()
    startOfToday.setHours(0, 0, 0, 0)
    return effectiveMeetings.filter((meeting) => {
      if (meeting.status === 'CANCELLED' || meeting.status === 'COMPLETED') return false
      const date = meetingDate(meeting.scheduledAt)
      return date ? date.getTime() >= startOfToday.getTime() : false
    }).length
  }, [effectiveMeetings])

  return {
    unreadThreads: Object.values(unreadByChannel).filter(Boolean).length,
    eventsToday: events.all.filter((event) => event.startDate <= todayIso && event.endDate >= todayIso).length,
    eventsThisWeek: events.all.length,
    meetingsUpcoming: upcomingMeetings,
    inHuddle: huddlePresence.length,
    onlineCount: viewerPresence.length,
    typingCount: typingPresence.length,
  }
}
